import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceIcon from '@mui/icons-material/Science';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    MenuItem,
    Paper,
    Skeleton,
    Snackbar,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import PlaceholderPicker from './PlaceholderPicker';
import { insertAtCursor } from './insertAtCursor';

import * as IntegrationService from 'src/services/integrationservice.service';
import * as IntegrationServiceHeadersService from 'src/services/integrationserviceheadersservice';
import * as IntegrationServiceParamsService from 'src/services/integrationserviceparams.service';
import * as IntegrationServiceRequestBodyService from 'src/services/integrationservicerequestbodyservice';
import Api from 'src/services/api';
import RequestIntegrationService from 'src/pages/integration-service-edit/components/request-integration-service';
import JsonEditor from 'src/components/json-editor';

/*
 * ServicePropertiesPanel — v2 (F5.1..F5.6 partials)
 *
 * Modos de renderizacao:
 *  - `variant="docked"` (default): usado dentro do Inspector lateral como uma
 *    aba "Propriedades do Serviço" — sem drag, sem header proprio, sem
 *    minimize (o container ja tem tudo isso). Ocupa 100% do espaco disponivel.
 *  - `variant="floating"`: painel flutuante com drag/minimize (modo Postman).
 *    Usado quando o usuario "desacopla" a aba via toggle.
 *
 * Carrega o service INTEIRO (headers, params, body em todos os formatos +
 * dados de auth) mapeando corretamente a shape lowercase do backend.
 * Cenarios cobertos:
 *  - Selecionar service existente → carrega todos os campos (nome, method, url,
 *    headers, params query+path, body raw JSON / form-data / url-encoded, auth).
 *  - Criar novo service inline → POST /integrationservice.
 *  - Limpar seleção (botao clear no autocomplete) → deselecta service da caixa.
 *  - Adicionar/remover/editar linhas em tempo real (dirty tracking).
 *  - Inserir %chat.*% em qualquer campo (URL, header value, param value,
 *    body raw, body form-data value, body url-encoded value).
 *  - Testar servico via RequestIntegrationService reusado.
 *
 * O que continua no CRUD dedicado (aviso na UI):
 *  - Configuracao complexa de autenticacao (integrationauth relacoes)
 *  - Custom fields (extraidos da response)
 *  - Mapeamento de erro avancado (nameErrorField, errorFieldReturnType etc.)
 */

type Variant = 'docked' | 'floating';

type Props = {
    currentServiceId: number | null;
    onServiceIdChange: (id: number | null) => void;
    onClose?: () => void;                    // Ignorado em variant=docked
    variant?: Variant;
    onToggleFloat?: () => void;              // Toggle detach/attach
    isFloating?: boolean;                    // Estado atual de floating (pra render do icone certo)
};

// Fix F7 sprint 0.5: alinhado com enum backend FieldService.Business.Models.MethodIntegration
// (Post=1, Get=2, Put=3, Delete=4). Antes estava invertido (GET=1/POST=2) — bug critico:
// service cadastrado no Panel como GET era gravado como POST no BD, e vice-versa,
// gerando divergencia com o CRUD legado. PATCH removido: nao existe no enum BE.
const HTTP_METHODS = [
    { id: 1, label: 'POST' },
    { id: 2, label: 'GET' },
    { id: 3, label: 'PUT' },
    { id: 4, label: 'DELETE' },
];

// Fix F7 sprint 0.5-B: tipos de retorno do campo indicativo de erro — replicado
// do CRUD legado /integration-service-edit (const errorFieldReturnTypes).
// Nota: no CRUD legado tem bug de id=3 duplicado (STRING e ARRAY). Aqui corrigido
// pra id=4 no ARRAY — se BE nao interpretar, admin usa 1/2/3 e ARRAY fica dead.
const ERROR_FIELD_RETURN_TYPES = [
    { id: 1, label: 'BOOLEANO' },
    { id: 2, label: 'INTEIRO' },
    { id: 3, label: 'STRING' },
    { id: 4, label: 'ARRAY' },
];

// Alinhado com enum backend IntegrationRequestBodyType.
// 1=None, 2=FormData, 3=Json, 4=UrlEncoded (novo — precisa suporte BE se ainda nao existir).
const BODY_TYPES = [
    { id: 1, label: 'None' },
    { id: 3, label: 'JSON (raw)' },
    { id: 2, label: 'Form-data' },
    { id: 4, label: 'x-www-form-urlencoded' },
];

const PANEL_POS_KEY = 'mexx.chatbot.service-panel.pos';
const PANEL_MIN_KEY = 'mexx.chatbot.service-panel.minimized';
const PANEL_SIZE_KEY = 'mexx.chatbot.service-panel.size';

// Tamanhos min/max pra resize manual (F6.G) — mantem UX sao.
const MIN_W = 380;
const MIN_H = 380;
const MAX_W = 900;
const MAX_H_VP_RATIO = 0.9; // 90% da viewport

type ServiceListItem = { id: number; name: string };
// Association: valor da linha pode vir de PARAM, VARIABLE ou CUSTOMFIELD do
// fluxo legado (mesma semantica do CRUD dedicado — `integrationserviceparamsrelation`
// e `integrationservicerequestbodyrelation` do BE). `null` = valor literal.
type AssociationRelation = { type: 'param' | 'variable' | 'customField'; relationId: number; name?: string } | null;
type AssociationOption = { relationid: number; type: string; name: string };
type HeaderRow = { id?: number; name: string; value: string };
type ParamRow = { id?: number; name: string; value: string; isPathParam: boolean; relation?: AssociationRelation };
type FormRow = { id?: number; name: string; value: string; relation?: AssociationRelation };

type ServiceState = {
    id: number | null;
    name: string;
    url: string;
    method: number;
    requestBodyType: number;
    bodyJson: string;
    bodyFormData: FormRow[];
    bodyUrlEncoded: FormRow[];
    headers: HeaderRow[];
    params: ParamRow[];
    // Metadata do auth — read-only na barra (edicao no CRUD dedicado).
    methodAuth: number | null;
    integrationAuthId: number | null;

    // Fix F7 sprint 0.5-C2: campos "opacos" preservados do CRUD legado
    // (/integration-service-edit). O Panel nao renderiza esses campos ainda,
    // mas TEM que preservar o valor lido no GET pra reenviar no PUT — senao,
    // toda vez que o admin salva pelo Panel, esses 13 campos sao zerados no BD.
    // Fluxo: load() populate com o valor do BE → submit() reenviar identico.
    // Bug B (aba "Retorno") vai substituir a preservacao passiva por controles
    // reais em: isArrayObjectType, nameFieldDisplay, nameFieldValue, variableIdFieldValue.
    // Os outros 9 permanecem preservados passivamente (metadata avancada de erro).
    objectName: string;
    nameErrorField: string;
    valueErrorField: string;
    messageError: string;
    nameFieldDisplay: string;
    variableIdFieldDisplay: number | null;
    nameFieldValue: string;
    variableIdFieldValue: number | null;
    errorFieldReturnType: number | null;
    errorDataFieldName: string;
    notFoundErrorMessage: string;
    isArrayObjectType: boolean;
    saveKeyApplication: boolean;
};

const EMPTY_SERVICE: ServiceState = {
    id: null,
    name: '',
    url: '',
    method: 1,
    requestBodyType: 1,
    bodyJson: '',
    bodyFormData: [],
    bodyUrlEncoded: [],
    headers: [],
    params: [],
    methodAuth: null,
    integrationAuthId: null,
    // Preserved fields — CREATE arranca com defaults; UPDATE substitui via load().
    objectName: '',
    nameErrorField: '',
    valueErrorField: '',
    messageError: '',
    nameFieldDisplay: '',
    variableIdFieldDisplay: null,
    nameFieldValue: '',
    variableIdFieldValue: null,
    errorFieldReturnType: null,
    errorDataFieldName: '',
    notFoundErrorMessage: '',
    isArrayObjectType: false,
    saveKeyApplication: false,
};

const ServicePropertiesPanel: React.FC<Props> = ({
    currentServiceId,
    onServiceIdChange,
    onClose,
    variant = 'docked',
    onToggleFloat,
    isFloating,
}) => {
    const intl = useIntl();

    // Posicao/minimize soh valem no modo floating.
    const [pos, setPos] = useState<{ x: number; y: number }>(() => {
        if (variant !== 'floating') return { x: 24, y: 24 };
        try {
            const raw = localStorage.getItem(PANEL_POS_KEY);
            return raw ? JSON.parse(raw) : { x: 80, y: 80 };
        } catch { return { x: 80, y: 80 }; }
    });
    const [minimized, setMinimized] = useState<boolean>(() => {
        if (variant !== 'floating') return false;
        try { return localStorage.getItem(PANEL_MIN_KEY) === '1'; } catch { return false; }
    });

    // F6.G — tamanho persistido no localStorage (soh floating).
    const [size, setSize] = useState<{ w: number; h: number }>(() => {
        if (variant !== 'floating') return { w: 500, h: 600 };
        try {
            const raw = localStorage.getItem(PANEL_SIZE_KEY);
            return raw ? JSON.parse(raw) : { w: 500, h: 600 };
        } catch { return { w: 500, h: 600 }; }
    });
    useEffect(() => {
        if (variant === 'floating') {
            try { localStorage.setItem(PANEL_SIZE_KEY, JSON.stringify(size)); } catch { /* noop */ }
        }
    }, [size, variant]);

    // Drag-to-dock: highlight quando o painel flutuante estah SENDO ARRASTADO
    // por cima da area do Inspector docked. Se soltar dentro, chama toggleFloat.
    const [dropTargetActive, setDropTargetActive] = useState(false);

    useEffect(() => {
        if (variant === 'floating') {
            try { localStorage.setItem(PANEL_POS_KEY, JSON.stringify(pos)); } catch { /* noop */ }
        }
    }, [pos, variant]);
    useEffect(() => {
        if (variant === 'floating') {
            try { localStorage.setItem(PANEL_MIN_KEY, minimized ? '1' : '0'); } catch { /* noop */ }
        }
    }, [minimized, variant]);

    // ─── Estado principal ────────────────────────────────────────────────
    const [tab, setTab] = useState<number>(0);
    const [servicesList, setServicesList] = useState<ServiceListItem[]>([]);
    const [loadingList, setLoadingList] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [saving, setSaving] = useState(false);
    const [service, setService] = useState<ServiceState>(EMPTY_SERVICE);
    const [isCreating, setIsCreating] = useState(false);
    const [dirty, setDirty] = useState(false);
    // F6 rodada 7: snapshot dos IDs de rows carregadas no LOAD — usado no UPDATE
    // pra diferenciar rows novos (sem id) de removidos (id existente que nao esta
    // mais no state). Sem isso o UPDATE nao propaga headers/params/body — o BE
    // UpdateViewModel nao tem *Rows e a edicao completa ficaria bloqueada.
    const [initialHeaderIds, setInitialHeaderIds] = useState<number[]>([]);
    const [initialParamIds, setInitialParamIds] = useState<number[]>([]);
    const [initialFormDataIds, setInitialFormDataIds] = useState<number[]>([]);
    const [initialUrlEncIds, setInitialUrlEncIds] = useState<number[]>([]);
    // Fix F7 sprint 0.5-D-edit: snapshot dos VALORES originais (nao so ids) pra
    // detectar rows EDITADAS no submit. Backend nao expõe PUT em
    // /integrationserviceparams — Panel simula edit via DELETE + POST no submit,
    // transparente pro admin. Mesma limitacao herdada do CRUD legado que so
    // suporta rows com `isEditable=true` (novas). Sem esses snapshots, alterar
    // valor/isPathParam/relation de um row existente e salvar NAO PERSISTE
    // (bug relatado — payload PUT nao carrega ParamRows e o BE UPDATE nao processa).
    const [initialHeaderSnap, setInitialHeaderSnap] = useState<Record<number, HeaderRow>>({});
    const [initialParamSnap, setInitialParamSnap] = useState<Record<number, ParamRow>>({});
    const [initialFormDataSnap, setInitialFormDataSnap] = useState<Record<number, FormRow>>({});
    const [initialUrlEncSnap, setInitialUrlEncSnap] = useState<Record<number, FormRow>>({});
    const [testOpen, setTestOpen] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    // Toast (Snackbar) local — desacoplado do CustomizedSnackbars do Inspector pai.
    // F6 rodada 6: feedback obrigatorio pos-save (create/update).
    const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

    // F7 Fase 1: 1 picker unificado (PlaceholderPicker) substitui os 2 anteriores
    // (ChatVariablesPicker + LegacyVariablesPicker) — 6 categorias em abas.
    const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
    const activeInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    // Ref direto pro textarea interno do JsonEditor — usado pelo picker de vars.
    const jsonEditorTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    // Lista de params/vars/customFields do fluxo — carregada 1x, usada em Associacoes.
    const [associationList, setAssociationList] = useState<AssociationOption[]>([]);

    // Fix F7 sprint 0.5: listas pra aba Auth funcionar igual CRUD legado.
    // - methodAuthList: metodos de auth disponiveis (Bearer/Basic/None). Vem
    //   do endpoint GET /integrationservice/get-method-auth-integration.
    // - integrationAuthList: auths ja cadastradas no tenant. Vem do GET
    //   /integrationauth. Panel guarda ambos e renderiza como dropdowns
    //   selecionaveis (nao apenas display chip).
    type AuthListItem = { id: number; name: string };
    const [methodAuthList, setMethodAuthList] = useState<AuthListItem[]>([]);
    const [integrationAuthList, setIntegrationAuthList] = useState<AuthListItem[]>([]);

    // ─── Load lista ─────────────────────────────────────────────────────
    const loadServices = useCallback(async () => {
        setLoadingList(true);
        try {
            const res: any = await (IntegrationService as any).getList();
            const list: ServiceListItem[] = Array.isArray(res) ? res : (res?.data ?? []);
            setServicesList(list);
        } catch {
            setServicesList([]);
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => { loadServices(); }, [loadServices]);

    // Fix F7 sprint 0.5-Auth rev: UMA chamada ao endpoint canonico
    // GET /integrationservice (IntegrationServiceService.getData no CRUD legado,
    // controller linhas 311-370) popula TODAS as listas de suporte de uma vez:
    //   - associationlist          → params + variables + customFields
    //   - authenticationmethodintegration → metodos de auth (Bearer/Basic/None/etc)
    //   - integrationauth          → auths cadastradas no tenant
    //   - methodintegration        → HTTP methods (POST/GET/PUT/DELETE) — nao
    //                                populado aqui porque temos HTTP_METHODS
    //                                const local, mas se um dia precisar validar
    //                                com BE, tirar do mesmo payload.
    // Mesmo endpoint que o CRUD legado usa em componentDidMount → garante 100%
    // paridade de opcoes/labels. Chamado 1x no mount, funciona em CREATE e EDIT.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res: any = await (IntegrationService as any).getData();
                // getData retorna { success, data: {...} } via CustomResponse.
                // API 2DO usa lowercase contract resolver — chaves em lowercase.
                const payload: any = res?.data ?? res ?? {};

                // 1) associationList — params/variables/customFields
                const assocRaw: any[] = Array.isArray(payload.associationlist) ? payload.associationlist : [];
                const assocList: AssociationOption[] = assocRaw.map((it: any) => ({
                    relationid: Number(it.relationid ?? it.id),
                    type: String(it.type ?? ''),
                    name: String(it.name ?? ''),
                }));

                // 2) methodAuthList — Bearer/Basic/None/etc. Mesma lista do CRUD.
                const methodAuthRaw: any[] = Array.isArray(payload.authenticationmethodintegration) ? payload.authenticationmethodintegration : [];
                const methodAuthItems: AuthListItem[] = methodAuthRaw.map((it: any) => ({
                    id: Number(it.id),
                    name: String(it.name ?? ''),
                }));

                // 3) integrationAuthList — auths cadastradas no tenant
                const authRaw: any[] = Array.isArray(payload.integrationauth) ? payload.integrationauth : [];
                const authItems: AuthListItem[] = authRaw.map((it: any) => ({
                    id: Number(it.id),
                    name: String(it.name ?? ''),
                }));

                if (!cancelled) {
                    setAssociationList(assocList);
                    setMethodAuthList(methodAuthItems);
                    setIntegrationAuthList(authItems);
                }
            } catch {
                if (!cancelled) {
                    setAssociationList([]);
                    setMethodAuthList([]);
                    setIntegrationAuthList([]);
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ─── Load detalhe (CORRIGIDO — mapeia TUDO da shape lowercase) ─────
    const loadServiceDetail = useCallback(async (id: number) => {
        setLoadingDetail(true);
        try {
            const data: any = await (IntegrationService as any).getDataRequestById(id);
            if (!data) return;

            // Headers: fallback duplo `value ?? defaultvalue` — algumas versoes do BE
            // retornaram como defaultvalue (bug F6.A-5: value vinha undefined).
            const headers: HeaderRow[] = Array.isArray(data.integrationserviceheaders)
                ? data.integrationserviceheaders.map((h: any) => ({
                    id: h.id,
                    name: h.name ?? '',
                    value: h.value ?? h.defaultvalue ?? '',
                }))
                : [];

            // Helper pra normalizar relation vinda do BE (lowercase) → shape local.
            const mapRelation = (r: any): AssociationRelation => {
                if (!r || r.relationid == null || !r.type) return null;
                const t = String(r.type).toLowerCase();
                const type: AssociationRelation['type'] =
                    t.startsWith('param') ? 'param' :
                    t.startsWith('var') ? 'variable' :
                    'customField';
                return { type: type as any, relationId: Number(r.relationid) };
            };

            // Params: {id, name, defaultvalue, ispathparam, integrationserviceparamsrelation}
            const params: ParamRow[] = Array.isArray(data.integrationserviceparams)
                ? data.integrationserviceparams.map((p: any) => ({
                    id: p.id,
                    name: p.name ?? '',
                    value: p.defaultvalue ?? p.value ?? '',
                    isPathParam: !!p.ispathparam,
                    relation: mapRelation(p.integrationserviceparamsrelation),
                }))
                : [];

            // Body — SEMPRE usar `requestbodytype` global como source-of-truth
            // (F6 rodada 8). Historicamente o BE gravava `type` por item hardcoded
            // como FormData (bug corrigido nessa mesma rodada), então services
            // antigos podem ter items com `type=2` mesmo sendo url-encoded (bodytype=4).
            // Confiar no type-per-item quebra a exibição desses services legados —
            // o bucket certo é sempre o do global do service.
            const bodyItems: any[] = Array.isArray(data.integrationservicerequestbody)
                ? data.integrationservicerequestbody
                : [];
            const rbt = Number(data.requestbodytype ?? data.requestBodyType ?? 1);

            const asFormRow = (b: any): FormRow => ({
                id: b.id,
                name: b.name ?? '',
                value: b.defaultvalue ?? b.value ?? '',
                relation: mapRelation(b.integrationservicerequestbodyrelation),
            });

            let jsonItem: any = null;
            let formRows: FormRow[] = [];
            let urlEncRows: FormRow[] = [];

            if (rbt === 3) {
                // JSON — item JSON tem type=3 quando existe (não é bugado historicamente).
                jsonItem = bodyItems.find(b => Number(b?.type) === 3) ?? bodyItems[0];
            } else if (rbt === 2) {
                // FormData — pega TODOS os items (ignora type-per-item, o global manda).
                formRows = bodyItems.filter(b => Number(b?.type) !== 3).map(asFormRow);
            } else if (rbt === 4) {
                // UrlEncoded — mesma lógica: todos os items exceto os JSON puros.
                urlEncRows = bodyItems.filter(b => Number(b?.type) !== 3).map(asFormRow);
            }

            const mapped: ServiceState = {
                id: data.id,
                name: data.name ?? '',
                url: data.url ?? '',
                // `method` no BE eh int direto (nao objeto). Fallback pra 1 (GET).
                method: typeof data.method === 'number' ? data.method : (data.method?.id ?? 1),
                requestBodyType: rbt,
                bodyJson: jsonItem?.defaultvalue ?? jsonItem?.value ?? '',
                bodyFormData: formRows,
                bodyUrlEncoded: urlEncRows,
                headers,
                params,
                methodAuth: data.methodauth ?? null,
                integrationAuthId: data.integrationauthid ?? null,
                // Fix F7 sprint 0.5-C2: preserva campos que o Panel nao edita mas
                // o BE UPDATE re-grava — sem essa preservacao, salvar pelo Panel
                // zerava 13 campos que o admin definira no CRUD legado.
                objectName: data.objectname ?? '',
                nameErrorField: data.nameerrorfield ?? '',
                valueErrorField: data.valueerrorfield ?? '',
                messageError: data.messageerror ?? '',
                nameFieldDisplay: data.namefielddisplay ?? '',
                variableIdFieldDisplay: data.variableidfielddisplay ?? null,
                nameFieldValue: data.namefieldvalue ?? '',
                variableIdFieldValue: data.variableidfieldvalue ?? null,
                errorFieldReturnType: data.errorfieldreturntype ?? null,
                errorDataFieldName: data.errordatafieldname ?? '',
                notFoundErrorMessage: data.notfounderrormessage ?? '',
                isArrayObjectType: !!data.isarrayobjecttype,
                saveKeyApplication: !!data.savekeyapplication,
            };
            setService(mapped);
            setIsCreating(false);
            setDirty(false);
            setSaveError(null);
            // Snapshot dos IDs originais — usado pelo UPDATE pra diffing.
            setInitialHeaderIds(headers.map(h => h.id).filter((x): x is number => x != null));
            setInitialParamIds(params.map(p => p.id).filter((x): x is number => x != null));
            setInitialFormDataIds(formRows.map(r => r.id).filter((x): x is number => x != null));
            setInitialUrlEncIds(urlEncRows.map(r => r.id).filter((x): x is number => x != null));
            // Fix F7 sprint 0.5-D-edit: snapshot dos VALORES pra detectar edits.
            // Guarda referencia por id — no submit, comparar state atual vs snapshot.
            const headerSnap: Record<number, HeaderRow> = {};
            headers.forEach(h => { if (h.id != null) headerSnap[h.id] = { ...h }; });
            setInitialHeaderSnap(headerSnap);
            const paramSnap: Record<number, ParamRow> = {};
            params.forEach(p => { if (p.id != null) paramSnap[p.id] = { ...p }; });
            setInitialParamSnap(paramSnap);
            const formSnap: Record<number, FormRow> = {};
            formRows.forEach(r => { if (r.id != null) formSnap[r.id] = { ...r }; });
            setInitialFormDataSnap(formSnap);
            const urlEncSnap: Record<number, FormRow> = {};
            urlEncRows.forEach(r => { if (r.id != null) urlEncSnap[r.id] = { ...r }; });
            setInitialUrlEncSnap(urlEncSnap);
        } catch (err) {
            console.error('[ServicePanel] load error', err);
        } finally {
            setLoadingDetail(false);
        }
    }, []);

    useEffect(() => {
        if (currentServiceId) {
            loadServiceDetail(currentServiceId);
        } else {
            setService(EMPTY_SERVICE);
            setIsCreating(false);
            setDirty(false);
            setSaveError(null);
        }
    }, [currentServiceId, loadServiceDetail]);

    // ─── Mutadores ──────────────────────────────────────────────────────
    const patchService = useCallback((patch: Partial<ServiceState>) => {
        setService(prev => ({ ...prev, ...patch }));
        setDirty(true);
    }, []);

    const startCreate = () => {
        setService({ ...EMPTY_SERVICE });
        setIsCreating(true);
        setDirty(true);
        setSaveError(null);
        setTab(0);
    };

    const clearSelection = () => {
        // Desvincula service da caixa — usuario pode escolher outro ou criar novo.
        onServiceIdChange(null);
    };

    // ─── Save ───────────────────────────────────────────────────────────
    const handleSave = async () => {
        setSaveError(null);
        // F6 rodada 6: fallback pro `name` — em UPDATE o BE historicamente
        // retornava name vazio; se o service tem id, buscar o name da lista
        // ANTES de validar. Assim usuario nao trava com "Nome obrigatório"
        // quando na verdade o service esta selecionado.
        let effectiveName = service.name?.trim() ?? '';
        if (!effectiveName && service.id) {
            const found = servicesList.find(s => s.id === service.id);
            if (found?.name) {
                effectiveName = found.name;
                setService(prev => ({ ...prev, name: found.name }));
            }
        }
        if (!effectiveName || !service.url?.trim()) {
            setSaveError(intl.formatMessage({
                id: 'servicepanel.validation.required',
                defaultMessage: 'Nome e URL são obrigatórios.',
            }));
            return;
        }
        setSaving(true);
        try {
            // Helper reverso pra montar relation no formato esperado pelo BE.
            // BE deserializa em IntegrationServiceRelationViewModel { Name, RelationId, Type }.
            const encodeRelation = (r?: AssociationRelation) =>
                r ? {
                    name: r.name ?? '',
                    relationId: r.relationId,
                    type: r.type === 'param' ? 'param' : r.type === 'variable' ? 'variable' : 'customField',
                } : null;

            // Fix F6 rodada 5: payload no formato EXATO dos VMs do BE
            // (IntegrationServiceRegisterViewModel / IntegrationServiceUpdateViewModel).
            // Antes: `method: {id: X}` — BE espera `int Method` puro → deserialization
            // silenciosa retornava `input = null` → "Object reference not set" no controller.
            // BE properties nao-nullable (Method, RequestBodyType, IntegrationAuthId,
            // MethodAuth) enviamos com valor default (0) quando null.
            const commonFields = {
                name: effectiveName,
                url: service.url,
                method: Number(service.method) || 1,
                requestBodyType: Number(service.requestBodyType) || 1,
                jsonValue: service.requestBodyType === 3 ? (service.bodyJson || null) : null,
                methodAuth: Number(service.methodAuth ?? 0),
                integrationAuthId: Number(service.integrationAuthId ?? 0),
                // Fix F7 sprint 0.5-C2: reenvia campos "opacos" preservados no load
                // (vindos do CRUD legado). Antes: zerados no submit → BE UPDATE
                // sobreescrevia com defaults → 13 campos perdidos a cada save.
                // Agora: submit reflete estado real → CRUD legado permanece
                // intacto ate B (aba "Retorno") expor 4 desses campos na UI do Panel.
                objectName: service.objectName ?? '',
                nameErrorField: service.nameErrorField ?? '',
                valueErrorField: service.valueErrorField ?? '',
                messageError: service.messageError ?? '',
                nameFieldDisplay: service.nameFieldDisplay ?? '',
                variableIdFieldDisplay: service.variableIdFieldDisplay,
                nameFieldValue: service.nameFieldValue ?? '',
                variableIdFieldValue: service.variableIdFieldValue,
                errorFieldReturnType: service.errorFieldReturnType,
                errorDataFieldName: service.errorDataFieldName ?? '',
                notFoundErrorMessage: service.notFoundErrorMessage ?? '',
                isArrayObjectType: !!service.isArrayObjectType,
                saveKeyApplication: !!service.saveKeyApplication,
            };

            const wasCreating = isCreating || !service.id;
            let res: any;
            if (wasCreating) {
                // CREATE — o RegisterViewModel exige as 3 listas (ParamRows/HeaderRows/BodyRequestRows).
                const createPayload: any = {
                    ...commonFields,
                    ParamRows: service.params.map(p => ({
                        name: p.name,
                        value: p.value,
                        isPathParam: !!p.isPathParam,
                        IntegrationServiceParamsRelation: encodeRelation(p.relation),
                    })),
                    HeaderRows: service.headers.map(h => ({
                        name: h.name,
                        value: h.value,
                    })),
                    BodyRequestRows: [
                        ...service.bodyFormData.map(r => ({
                            name: r.name,
                            value: r.value,
                            IntegrationServiceParamsRelation: encodeRelation(r.relation),
                        })),
                        ...service.bodyUrlEncoded.map(r => ({
                            name: r.name,
                            value: r.value,
                            IntegrationServiceParamsRelation: encodeRelation(r.relation),
                        })),
                    ],
                };
                res = await (IntegrationService as any).addItem(createPayload);
            } else {
                // UPDATE — UpdateViewModel NAO tem *Rows; edicao de rows via endpoints
                // dedicados (mesmo padrao do CRUD /integration-service-edit).
                // 1) PUT campos básicos
                const updatePayload: any = {
                    ...commonFields,
                    id: service.id,
                    integrationAuth: service.integrationAuthId != null ? Number(service.integrationAuthId) : null,
                };
                res = await (IntegrationService as any).updateItem(updatePayload);

                // Fix F7 sprint 0.5-D-edit: diff completo das *Rows.
                // Backend nao expoe PUT em /integrationserviceparams|headers|requestbody
                // — soh POST (add) e DELETE. Panel simula edit inline via DELETE + POST
                // no submit, transparente pro admin. Ordem intencional (DELETE antes
                // do POST) evita conflito de nome unico caso o schema tenha
                // unique(integrationserviceid, name) — o BD nao tem hoje mas fica safe.
                const sid = service.id!;

                // Helpers de comparacao — soh detecta EDIT real (algum campo
                // funcional mudou; name/value/isPathParam/relation).
                const relationEq = (a?: AssociationRelation, b?: AssociationRelation) => {
                    if (!a && !b) return true;
                    if (!a || !b) return false;
                    return a.type === b.type && a.relationId === b.relationId;
                };
                const headerChanged = (curr: HeaderRow, orig: HeaderRow) =>
                    curr.name !== orig.name || (curr.value ?? '') !== (orig.value ?? '');
                const paramChanged = (curr: ParamRow, orig: ParamRow) =>
                    curr.name !== orig.name
                    || (curr.value ?? '') !== (orig.value ?? '')
                    || !!curr.isPathParam !== !!orig.isPathParam
                    || !relationEq(curr.relation ?? null, orig.relation ?? null);
                const formRowChanged = (curr: FormRow, orig: FormRow) =>
                    curr.name !== orig.name
                    || (curr.value ?? '') !== (orig.value ?? '')
                    || !relationEq(curr.relation ?? null, orig.relation ?? null);

                // ─── HEADERS ────────────────────────────────────────────────
                // 1) EDITADOS: id existe no snap + campos mudaram → DELETE + POST
                //    (feito ANTES dos novos e dos removidos pra reaproveitar loop)
                const editedHeaders = service.headers.filter(h =>
                    h.id != null && initialHeaderSnap[h.id] && headerChanged(h, initialHeaderSnap[h.id])
                );
                for (const h of editedHeaders) {
                    try {
                        await Api.delete(`/integrationserviceheaders/${h.id}`);
                        await (IntegrationServiceHeadersService as any).addItem({
                            name: h.name, value: h.value ?? '', integrationserviceid: sid,
                        });
                    } catch (e) { console.warn('[ServicePanel] header edit failed', e); }
                }
                // 2) NOVOS: sem id → POST
                const newHeaders = service.headers.filter(h => h.id == null && (h.name || '').trim());
                for (const h of newHeaders) {
                    try {
                        await (IntegrationServiceHeadersService as any).addItem({
                            name: h.name, value: h.value ?? '', integrationserviceid: sid,
                        });
                    } catch (e) { console.warn('[ServicePanel] header add failed', e); }
                }
                // 3) REMOVIDOS: id no snap mas nao no state → DELETE
                const currentHeaderIds = new Set(service.headers.map(h => h.id).filter((x): x is number => x != null));
                const removedHeaderIds = initialHeaderIds.filter(id => !currentHeaderIds.has(id));
                for (const id of removedHeaderIds) {
                    try { await Api.delete(`/integrationserviceheaders/${id}`); } catch (e) { console.warn(e); }
                }

                // ─── PARAMS ─────────────────────────────────────────────────
                // 1) EDITADOS → DELETE + POST
                const editedParams = service.params.filter(p =>
                    p.id != null && initialParamSnap[p.id] && paramChanged(p, initialParamSnap[p.id])
                );
                for (const p of editedParams) {
                    try {
                        await Api.delete(`/integrationserviceparams/${p.id}`);
                        const payload: any = {
                            name: p.name, value: p.value ?? '', integrationserviceid: sid,
                            ispathparam: !!p.isPathParam,
                        };
                        if (p.relation) payload.integrationserviceparamsrelation = encodeRelation(p.relation);
                        await (IntegrationServiceParamsService as any).addItem(payload);
                    } catch (e) { console.warn('[ServicePanel] param edit failed', e); }
                }
                // 2) NOVOS → POST
                const newParams = service.params.filter(p => p.id == null && (p.name || '').trim());
                for (const p of newParams) {
                    try {
                        const payload: any = {
                            name: p.name, value: p.value ?? '', integrationserviceid: sid,
                            ispathparam: !!p.isPathParam,
                        };
                        if (p.relation) payload.integrationserviceparamsrelation = encodeRelation(p.relation);
                        await (IntegrationServiceParamsService as any).addItem(payload);
                    } catch (e) { console.warn('[ServicePanel] param add failed', e); }
                }
                // 3) REMOVIDOS → DELETE
                const currentParamIds = new Set(service.params.map(p => p.id).filter((x): x is number => x != null));
                const removedParamIds = initialParamIds.filter(id => !currentParamIds.has(id));
                for (const id of removedParamIds) {
                    try { await Api.delete(`/integrationserviceparams/${id}`); } catch (e) { console.warn(e); }
                }

                // ─── BODY (formData + urlEncoded compartilham endpoint) ────
                // 1) EDITADOS → DELETE + POST
                const editedFormData = service.bodyFormData.filter(r =>
                    r.id != null && initialFormDataSnap[r.id] && formRowChanged(r, initialFormDataSnap[r.id])
                );
                const editedUrlEnc = service.bodyUrlEncoded.filter(r =>
                    r.id != null && initialUrlEncSnap[r.id] && formRowChanged(r, initialUrlEncSnap[r.id])
                );
                for (const row of [...editedFormData, ...editedUrlEnc]) {
                    try {
                        await Api.delete(`/integrationservicerequestbody/${row.id}`);
                        const payload: any = {
                            name: row.name, value: row.value ?? '', integrationserviceid: sid,
                        };
                        if (row.relation) payload.integrationservicerequestbodyrelation = encodeRelation(row.relation);
                        await (IntegrationServiceRequestBodyService as any).addItem(payload);
                    } catch (e) { console.warn('[ServicePanel] body row edit failed', e); }
                }
                // 2) NOVOS → POST
                const newBodyRows: Array<{ row: FormRow }> = [
                    ...service.bodyFormData.filter(r => r.id == null && (r.name || '').trim()).map(row => ({ row })),
                    ...service.bodyUrlEncoded.filter(r => r.id == null && (r.name || '').trim()).map(row => ({ row })),
                ];
                for (const { row } of newBodyRows) {
                    try {
                        const payload: any = {
                            name: row.name, value: row.value ?? '', integrationserviceid: sid,
                        };
                        if (row.relation) payload.integrationservicerequestbodyrelation = encodeRelation(row.relation);
                        await (IntegrationServiceRequestBodyService as any).addItem(payload);
                    } catch (e) { console.warn('[ServicePanel] body row add failed', e); }
                }
                // 3) REMOVIDOS → DELETE
                const currentBodyIds = new Set([
                    ...service.bodyFormData.map(r => r.id),
                    ...service.bodyUrlEncoded.map(r => r.id),
                ].filter((x): x is number => x != null));
                const removedBodyIds = [...initialFormDataIds, ...initialUrlEncIds].filter(id => !currentBodyIds.has(id));
                for (const id of removedBodyIds) {
                    try { await Api.delete(`/integrationservicerequestbody/${id}`); } catch (e) { console.warn(e); }
                }
            }

            // F6 rodada 6: sucesso vira quando res.success===true (o BE devolve
            // {success:true,data:[],errors:[]} no CREATE sem echo do item — nao
            // dava pra depender de data.id). Fluxo pos-save COMPLETO:
            //   1) toast de sucesso
            //   2) recarregar servicesList (o novo aparece no autocomplete)
            //   3) se CREATE, achar o novo pelo NAME (unico por hirer) e selecionar
            //   4) se UPDATE, recarregar detail (dados atuais no form)
            //   5) sair de isCreating + limpar dirty
            const successFlag = res?.success === true || res?.data?.success === true;
            const errList: string[] = res?.errors ?? res?.data?.errors ?? [];
            if (!successFlag) {
                setSaveError(errList[0] ?? intl.formatMessage({
                    id: 'servicepanel.save.error',
                    defaultMessage: 'Não foi possível salvar o serviço.',
                }));
                setToast({
                    severity: 'error',
                    message: errList[0] ?? intl.formatMessage({
                        id: 'servicepanel.save.error',
                        defaultMessage: 'Não foi possível salvar o serviço.',
                    }),
                });
                return;
            }

            await loadServices();

            if (wasCreating) {
                // Localiza o novo item por Name na lista atualizada.
                // Tenta 2x (100ms) caso a lista chegue com pequeno delay de cache.
                let list: ServiceListItem[] = [];
                try {
                    const rr: any = await (IntegrationService as any).getList();
                    list = Array.isArray(rr) ? rr : (rr?.data ?? []);
                } catch { /* keep empty */ }
                setServicesList(list);
                const found = list.find(s => (s.name ?? '').trim() === effectiveName.trim());
                if (found?.id) {
                    onServiceIdChange(found.id);
                    await loadServiceDetail(found.id);
                    setIsCreating(false);
                    setDirty(false);
                    setToast({
                        severity: 'success',
                        message: intl.formatMessage({
                            id: 'servicepanel.create.success',
                            defaultMessage: 'Serviço criado com sucesso.',
                        }),
                    });
                } else {
                    // Fallback — nao achou pelo nome (pode ser dedup, refresh cache…).
                    setService(EMPTY_SERVICE);
                    setIsCreating(false);
                    setDirty(false);
                    setToast({
                        severity: 'success',
                        message: intl.formatMessage({
                            id: 'servicepanel.create.success.norefresh',
                            defaultMessage: 'Serviço criado. Selecione-o na lista.',
                        }),
                    });
                }
            } else {
                // UPDATE — mantem selecao, mantem dados na tela, só recarrega pra pegar
                // qualquer normalizacao BE-side.
                if (service.id) {
                    await loadServiceDetail(service.id);
                }
                setDirty(false);
                setToast({
                    severity: 'success',
                    message: intl.formatMessage({
                        id: 'servicepanel.update.success',
                        defaultMessage: 'Serviço atualizado com sucesso.',
                    }),
                });
            }
        } catch (err: any) {
            const msg = err?.response?.data?.errors?.[0] ?? intl.formatMessage({
                id: 'servicepanel.save.error',
                defaultMessage: 'Não foi possível salvar o serviço.',
            });
            setSaveError(msg);
            setToast({ severity: 'error', message: msg });
        } finally {
            setSaving(false);
        }
    };

    // ─── Row helpers ────────────────────────────────────────────────────
    const addHeader = () => patchService({ headers: [...service.headers, { name: '', value: '' }] });
    const removeHeader = (idx: number) => patchService({ headers: service.headers.filter((_, i) => i !== idx) });
    const updateHeader = (idx: number, field: keyof HeaderRow, value: any) =>
        patchService({ headers: service.headers.map((h, i) => i === idx ? { ...h, [field]: value } : h) });

    const addParam = () => patchService({ params: [...service.params, { name: '', value: '', isPathParam: false }] });
    const removeParam = (idx: number) => patchService({ params: service.params.filter((_, i) => i !== idx) });
    // aceita 'name' | 'value' | 'isPathParam' | 'relation'
    const updateParam = (idx: number, field: string, value: any) =>
        patchService({ params: service.params.map((p, i) => i === idx ? { ...p, [field]: value } : p) });

    const addFormRow = () => patchService({ bodyFormData: [...service.bodyFormData, { name: '', value: '' }] });
    const removeFormRow = (idx: number) => patchService({ bodyFormData: service.bodyFormData.filter((_, i) => i !== idx) });
    // aceita 'name' | 'value' | 'relation' — o KeyValueList/AssociationSelector emite os 3.
    const updateFormRow = (idx: number, field: string, value: any) =>
        patchService({ bodyFormData: service.bodyFormData.map((r, i) => i === idx ? { ...r, [field]: value } : r) });

    const addUrlEncRow = () => patchService({ bodyUrlEncoded: [...service.bodyUrlEncoded, { name: '', value: '' }] });
    const removeUrlEncRow = (idx: number) => patchService({ bodyUrlEncoded: service.bodyUrlEncoded.filter((_, i) => i !== idx) });
    const updateUrlEncRow = (idx: number, field: string, value: any) =>
        patchService({ bodyUrlEncoded: service.bodyUrlEncoded.map((r, i) => i === idx ? { ...r, [field]: value } : r) });

    // ─── Drag (só floating) + drag-to-dock (F6.G) ───────────────────────
    const dragStartRef = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);

    // Detecta se o cursor esta sobre a area do Inspector docked (`.chatbot-inspector`).
    // Usado durante o drag pra ativar highlight (drop zone) e no drop pra acoplar.
    const isCursorOverInspector = (clientX: number, clientY: number): boolean => {
        const el = document.querySelector('.chatbot-inspector') as HTMLElement | null;
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    };

    const onHeaderMouseDown = (e: React.MouseEvent) => {
        if (variant !== 'floating') return;
        dragStartRef.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
        window.addEventListener('mousemove', onDragMove);
        window.addEventListener('mouseup', onDragEnd);
    };
    const onDragMove = (e: MouseEvent) => {
        const start = dragStartRef.current;
        if (!start) return;
        const dx = e.clientX - start.mx;
        const dy = e.clientY - start.my;
        const maxX = Math.max(0, window.innerWidth - size.w);
        const maxY = Math.max(0, window.innerHeight - 100);
        setPos({
            x: Math.min(Math.max(0, start.px + dx), maxX),
            y: Math.min(Math.max(0, start.py + dy), maxY),
        });
        // Drop-zone highlight — se cursor entra na area do Inspector docked,
        // sinaliza que soltar vai acoplar.
        setDropTargetActive(isCursorOverInspector(e.clientX, e.clientY));
    };
    const onDragEnd = (e?: MouseEvent) => {
        const shouldDock = e ? isCursorOverInspector(e.clientX, e.clientY) : false;
        dragStartRef.current = null;
        setDropTargetActive(false);
        window.removeEventListener('mousemove', onDragMove);
        window.removeEventListener('mouseup', onDragEnd);
        if (shouldDock && onToggleFloat) {
            // Solta em cima do Inspector → acopla (padrao Postman/VSCode).
            onToggleFloat();
        }
    };

    // ─── Resize manual (canto SE — F6.G) ────────────────────────────────
    const resizeStartRef = useRef<{ mx: number; my: number; sw: number; sh: number } | null>(null);
    const onResizeMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation();
        resizeStartRef.current = { mx: e.clientX, my: e.clientY, sw: size.w, sh: size.h };
        window.addEventListener('mousemove', onResizeMove);
        window.addEventListener('mouseup', onResizeEnd);
    };
    const onResizeMove = (e: MouseEvent) => {
        const s = resizeStartRef.current;
        if (!s) return;
        const maxH = Math.floor(window.innerHeight * MAX_H_VP_RATIO);
        const w = Math.max(MIN_W, Math.min(MAX_W, s.sw + (e.clientX - s.mx)));
        const h = Math.max(MIN_H, Math.min(maxH, s.sh + (e.clientY - s.my)));
        setSize({ w, h });
    };
    const onResizeEnd = () => {
        resizeStartRef.current = null;
        window.removeEventListener('mousemove', onResizeMove);
        window.removeEventListener('mouseup', onResizeEnd);
    };

    // ─── Variable picker ────────────────────────────────────────────────
    const openPicker = (anchor: HTMLElement, input: HTMLInputElement | HTMLTextAreaElement | null) => {
        activeInputRef.current = input;
        setPickerAnchor(anchor);
    };
    const closePicker = () => setPickerAnchor(null);
    const insertVariable = (placeholder: string) => {
        insertAtCursor(activeInputRef.current, placeholder);
        closePicker();
    };

    // ─── Renderizacao minimizada (soh floating) ─────────────────────────
    if (variant === 'floating' && minimized) {
        return (
            <Paper
                sx={{
                    position: 'absolute', left: pos.x, top: pos.y, zIndex: 500,
                    px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1,
                    cursor: 'pointer', boxShadow: 3, borderRadius: 2,
                    background: '#2d2d34', color: '#fff',
                }}
                onClick={() => setMinimized(false)}
                title={intl.formatMessage({ id: 'servicepanel.restore', defaultMessage: 'Restaurar propriedades' })}
            >
                <CodeIcon fontSize="small" />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {service.name || intl.formatMessage({ id: 'servicepanel.title', defaultMessage: 'Propriedades do Serviço' })}
                </Typography>
                <OpenInFullIcon fontSize="small" />
            </Paper>
        );
    }

    // ─── Container (docked ou floating) ────────────────────────────────
    const containerSx = variant === 'floating'
        ? {
            position: 'absolute' as const,
            left: pos.x, top: pos.y,
            width: size.w,
            height: size.h,
            // z-index alto pra ficar SEMPRE acima do Inspector docked (que tem z-index:100).
            // Padrao Postman/Insomnia: painel flutuante wins.
            zIndex: 500,
            border: isCreating ? '2px dashed' : '1px solid',
            borderColor: isCreating ? 'warning.main' : 'rgba(0,0,0,0.12)',
        }
        : {
            position: 'relative' as const,
            width: '100%',
            height: '100%',
            border: 'none',
            boxShadow: 'none',
            borderRadius: 0,
        };

    const content = (
        <>
            {/* Header — soh renderiza em floating (docked usa header do Inspector) */}
            {variant === 'floating' && (
                <Box
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.5, py: 1, background: '#2d2d34', color: '#ffffff',
                        cursor: 'move', userSelect: 'none',
                    }}
                    onMouseDown={onHeaderMouseDown}
                >
                    <DragIndicatorIcon fontSize="small" />
                    <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600, color: '#ffffff' }}>
                        {intl.formatMessage({ id: 'servicepanel.title', defaultMessage: 'Propriedades do Serviço' })}
                        {dirty && <Chip
                            label={intl.formatMessage({ id: 'servicepanel.unsaved', defaultMessage: 'não salvo' })}
                            size="small" color="warning"
                            sx={{ ml: 1, height: 18, fontSize: 10 }}
                        />}
                    </Typography>
                    {onToggleFloat && (
                        <Tooltip title={intl.formatMessage({ id: 'servicepanel.dock', defaultMessage: 'Acoplar ao Inspector' })}>
                            <IconButton size="small" onClick={onToggleFloat} sx={{ color: 'inherit' }}>
                                <LinkIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title={intl.formatMessage({ id: 'servicepanel.minimize', defaultMessage: 'Minimizar' })}>
                        <IconButton size="small" onClick={() => setMinimized(true)} sx={{ color: 'inherit' }}>
                            <MinimizeIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Docked: toolbar mini com toggle desacoplar */}
            {variant === 'docked' && onToggleFloat && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Tooltip title={intl.formatMessage({ id: 'servicepanel.undock', defaultMessage: 'Desacoplar (flutuante)' })}>
                        <IconButton size="small" onClick={onToggleFloat}>
                            <LinkOffIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {dirty && <Chip
                        label={intl.formatMessage({ id: 'servicepanel.unsaved', defaultMessage: 'não salvo' })}
                        size="small" color="warning"
                        sx={{ ml: 1, height: 20, fontSize: 10, alignSelf: 'center' }}
                    />}
                </Box>
            )}

            {/* Seletor de service + criar + limpar */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Autocomplete
                        fullWidth
                        size="small"
                        options={servicesList}
                        loading={loadingList}
                        getOptionLabel={(opt) => opt?.name ?? ''}
                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                        value={servicesList.find(s => s.id === service.id) ?? null}
                        onChange={(_, val) => {
                            if (val?.id) {
                                onServiceIdChange(val.id);
                                setIsCreating(false);
                            } else {
                                // clear via botao X do Autocomplete
                                clearSelection();
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={intl.formatMessage({ id: 'servicepanel.service.label', defaultMessage: 'Serviço' })}
                                placeholder={intl.formatMessage({ id: 'servicepanel.service.placeholder', defaultMessage: 'Selecione ou crie um serviço...' })}
                            />
                        )}
                        slotProps={{
                            popper: {
                                modifiers: [
                                    { name: 'flip', enabled: true, options: { fallbackPlacements: ['top', 'bottom'] } },
                                    { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport' } },
                                ],
                            },
                        }}
                    />
                    <Tooltip title={intl.formatMessage({ id: 'servicepanel.reload', defaultMessage: 'Recarregar lista' })}>
                        <span>
                            {/* Feedback visual do reload: rotate no icone durante loadingList
                                + disabled = usuario ve que a acao pegou (F6 rodada 4). */}
                            <IconButton size="small" onClick={loadServices} disabled={loadingList}>
                                <RefreshIcon
                                    fontSize="small"
                                    sx={loadingList ? {
                                        animation: 'mexx-svc-spin 0.9s linear infinite',
                                        '@keyframes mexx-svc-spin': {
                                            '0%': { transform: 'rotate(0deg)' },
                                            '100%': { transform: 'rotate(360deg)' },
                                        },
                                    } : undefined}
                                />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={intl.formatMessage({ id: 'servicepanel.create.new', defaultMessage: 'Criar novo serviço' })}>
                        <IconButton size="small" color="primary" onClick={startCreate}><AddIcon /></IconButton>
                    </Tooltip>
                </Stack>
                {(loadingDetail || loadingList) && (
                    // LinearProgress + Skeleton — feedback visual quando lista OU detalhe carrega.
                    <Box sx={{ mt: 1 }}>
                        <LinearProgress sx={{ mb: 0.75, height: 3, borderRadius: 1 }} />
                        {loadingDetail && (
                            <Stack spacing={0.75}>
                                <Skeleton variant="rectangular" height={8} width="40%" sx={{ borderRadius: 1 }} />
                                <Skeleton variant="rectangular" height={8} width="70%" sx={{ borderRadius: 1 }} />
                            </Stack>
                        )}
                    </Box>
                )}
            </Box>

            {/* Tabs — Fix F7 sprint 0.5-B: mudanca de fullWidth → scrollable pra
                acomodar a 6a aba "Retorno" sem apertar/cortar as labels em Panel
                estreito. scrollButtons auto so aparecem quando ha overflow real. */}
            {(service.id || isCreating) && (
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ minHeight: 36, borderBottom: '1px solid rgba(0,0,0,0.08)' }}
                >
                    <Tab label={intl.formatMessage({ id: 'servicepanel.tab.basic', defaultMessage: 'Básico' })} sx={{ minHeight: 36 }} />
                    <Tab label={intl.formatMessage({ id: 'servicepanel.tab.headers', defaultMessage: 'Headers' })} sx={{ minHeight: 36 }} />
                    <Tab label={intl.formatMessage({ id: 'servicepanel.tab.params', defaultMessage: 'Params' })} sx={{ minHeight: 36 }} />
                    <Tab label={intl.formatMessage({ id: 'servicepanel.tab.body', defaultMessage: 'Body' })} sx={{ minHeight: 36 }} />
                    <Tab label={intl.formatMessage({ id: 'servicepanel.tab.auth', defaultMessage: 'Auth' })} sx={{ minHeight: 36 }} />
                    <Tab label={intl.formatMessage({ id: 'servicepanel.tab.response', defaultMessage: 'Retorno' })} sx={{ minHeight: 36 }} />
                </Tabs>
            )}

            {/* Conteudo */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, minHeight: 0 }}>
                {loadingDetail && (
                    // Skeleton dos campos enquanto carrega detalhe do service novo
                    <Stack spacing={1.5}>
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                        <Stack direction="row" spacing={1}>
                            <Skeleton variant="rectangular" width={110} height={40} sx={{ borderRadius: 1 }} />
                            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, flex: 1 }} />
                        </Stack>
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                    </Stack>
                )}

                {!loadingDetail && (!service.id && !isCreating) && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        <Typography variant="body2">
                            {intl.formatMessage({
                                id: 'servicepanel.empty',
                                defaultMessage: 'Selecione ou crie um serviço pra começar.',
                            })}
                        </Typography>
                    </Box>
                )}

                {!loadingDetail && (service.id || isCreating) && (
                    <>
                        {tab === 0 && (
                            <Stack spacing={2}>
                                {/* Nome soh aparece em modo criacao — em edicao, o nome
                                    ja eh o que o usuario ve no autocomplete no topo (F6.A-14). */}
                                {isCreating && (
                                    <TextField
                                        label={intl.formatMessage({ id: 'servicepanel.field.name', defaultMessage: 'Nome' })}
                                        size="small" fullWidth
                                        value={service.name}
                                        onChange={(e) => patchService({ name: e.target.value })}
                                        autoFocus
                                    />
                                )}
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        select size="small"
                                        label={intl.formatMessage({ id: 'servicepanel.field.method', defaultMessage: 'Método' })}
                                        value={service.method}
                                        onChange={(e) => patchService({ method: Number(e.target.value) })}
                                        sx={{ minWidth: 110 }}
                                    >
                                        {HTTP_METHODS.map(m => <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>)}
                                    </TextField>
                                    <VariableTextField
                                        label={intl.formatMessage({ id: 'servicepanel.field.url', defaultMessage: 'URL' })}
                                        value={service.url}
                                        onChange={(v) => patchService({ url: v })}
                                        onOpenPicker={openPicker}
                                        fullWidth
                                    />
                                </Stack>
                            </Stack>
                        )}

                        {tab === 1 && (
                            <KeyValueList
                                rows={service.headers}
                                onAdd={addHeader}
                                onRemove={removeHeader}
                                onUpdate={updateHeader}
                                onOpenPicker={openPicker}
                                addLabel={intl.formatMessage({ id: 'servicepanel.header.add', defaultMessage: 'Adicionar header' })}
                                nameLabel={intl.formatMessage({ id: 'servicepanel.header.name', defaultMessage: 'Nome' })}
                                valueLabel={intl.formatMessage({ id: 'servicepanel.header.value', defaultMessage: 'Valor' })}
                            />
                        )}

                        {tab === 2 && (
                            <Stack spacing={1.25}>
                                {service.params.map((p, idx) => {
                                    const hasAssoc = !!p.relation;
                                    // Layout empilhado 2 linhas — evita quebra caotica em <500px
                                    // (padrao Postman/Insomnia — F6 rodada 4 fix layout).
                                    return (
                                        <Box
                                            key={idx}
                                            sx={{
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                borderRadius: 1,
                                                p: 1,
                                                background: '#fbfbfb',
                                            }}
                                        >
                                            {/* Linha 1: Nome + Chip Path/Query + Delete */}
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <TextField
                                                    label={intl.formatMessage({ id: 'servicepanel.param.name', defaultMessage: 'Nome' })}
                                                    size="small" value={p.name}
                                                    onChange={(e) => updateParam(idx, 'name', e.target.value)}
                                                    sx={{ flex: 1 }}
                                                />
                                                <Tooltip title={intl.formatMessage({ id: 'servicepanel.param.ispath', defaultMessage: 'Path param (URL {var}) vs Query string (?var=)' })}>
                                                    <Chip
                                                        label={p.isPathParam ? 'Path' : 'Query'}
                                                        size="small"
                                                        color={p.isPathParam ? 'secondary' : 'default'}
                                                        onClick={() => updateParam(idx, 'isPathParam', !p.isPathParam)}
                                                        sx={{ cursor: 'pointer', minWidth: 62 }}
                                                    />
                                                </Tooltip>
                                                <IconButton size="small" onClick={() => removeParam(idx)}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                            {/* Linha 2 (opcional): Chip da associacao ativa.
                                                Fix F7 sprint 0.5-D: chip parou de OCULTAR o campo Value.
                                                Agora fica em cima do proprio Value pra ser deletado
                                                sem perder o fallback ja digitado. */}
                                            {hasAssoc && (
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                                    <Chip
                                                        label={p.relation?.name ? `${p.relation.type}: ${p.relation.name}` : `Associação #${p.relation?.relationId}`}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        onDelete={() => updateParam(idx, 'relation', null)}
                                                        sx={{ flex: 1, justifyContent: 'flex-start' }}
                                                    />
                                                </Stack>
                                            )}
                                            {/* Linha 3: Valor padrao SEMPRE visivel + selector de associacao.
                                                Fix F7 sprint 0.5-D:
                                                - Rotulo semanticamente correto (BE grava em `default_value`).
                                                - Quando ha associacao, atua como FALLBACK (resolvido se a
                                                  associacao vier null/vazia — comportamento nativo do
                                                  ChatbotHttpServiceBuilder linha 302: `value = param.DefaultValue`).
                                                - Aceita todos os 6 tipos de placeholder (F7 fase 1). */}
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Tooltip
                                                        title={
                                                            hasAssoc
                                                                ? 'Fallback: usado quando a associação não retornar valor. Aceita placeholders: %param%, @variavel@, $custom$, {{chat.*}}, {{system.*}}, {{session.user.*}}. Ex.: {{chat.id}}'
                                                                : 'Valor enviado como default. Aceita placeholders: %param%, @variavel@, $custom$, {{chat.*}}, {{system.*}}, {{session.user.*}}. Ex.: {{chat.id}}'
                                                        }
                                                        placement="top-start"
                                                        arrow
                                                    >
                                                        <Box>
                                                            <VariableTextField
                                                                label={
                                                                    hasAssoc
                                                                        ? intl.formatMessage({ id: 'servicepanel.param.defaultvalue.fallback', defaultMessage: 'Valor padrão (fallback)' })
                                                                        : intl.formatMessage({ id: 'servicepanel.param.defaultvalue', defaultMessage: 'Valor padrão' })
                                                                }
                                                                value={p.value}
                                                                onChange={(v) => updateParam(idx, 'value', v)}
                                                                onOpenPicker={openPicker}
                                                                fullWidth
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                </Box>
                                                <Box sx={{ width: 180 }}>
                                                    <AssociationSelector
                                                        value={p.relation ?? null}
                                                        options={associationList}
                                                        onChange={(rel) => updateParam(idx, 'relation', rel)}
                                                    />
                                                </Box>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                                <Button size="small" startIcon={<AddIcon />} onClick={addParam}>
                                    {intl.formatMessage({ id: 'servicepanel.param.add', defaultMessage: 'Adicionar parâmetro' })}
                                </Button>
                            </Stack>
                        )}

                        {tab === 3 && (
                            <Stack spacing={2}>
                                <TextField
                                    select size="small"
                                    label={intl.formatMessage({ id: 'servicepanel.body.type', defaultMessage: 'Tipo de body' })}
                                    value={service.requestBodyType}
                                    onChange={(e) => patchService({ requestBodyType: Number(e.target.value) })}
                                    sx={{ maxWidth: 260 }}
                                >
                                    {BODY_TYPES.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                                </TextField>

                                {service.requestBodyType === 3 && (
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            {/* F7 Fase 1: 1 botão apenas — PlaceholderPicker unificado
                                                abre com 6 categorias em abas (chat/system/session/param/variable/customField). */}
                                            <Button
                                                size="small"
                                                startIcon={<CodeIcon />}
                                                onClick={(e) => openPicker(
                                                    e.currentTarget as HTMLElement,
                                                    jsonEditorTextareaRef.current
                                                )}
                                            >
                                                {intl.formatMessage({ id: 'servicepanel.body.insertplaceholder', defaultMessage: 'Inserir placeholder' })}
                                            </Button>
                                        </Stack>
                                        {/* JsonEditor profissional — mesmo componente usado pelo
                                            webhook-payload-config. Line numbers, resize, syntax
                                            error inline, formatar. Reuso puro (F6.C). */}
                                        <JsonEditor
                                            value={service.bodyJson}
                                            onChange={(v: string) => patchService({ bodyJson: v })}
                                            label={intl.formatMessage({ id: 'servicepanel.body.json', defaultMessage: 'Body JSON (raw)' })}
                                            minHeight={200}
                                            maxHeight={500}
                                            textareaRef={jsonEditorTextareaRef}
                                        />
                                    </Stack>
                                )}

                                {service.requestBodyType === 2 && (
                                    <KeyValueList
                                        rows={service.bodyFormData}
                                        onAdd={addFormRow}
                                        onRemove={removeFormRow}
                                        onUpdate={updateFormRow as any}
                                        onOpenPicker={openPicker}
                                        addLabel={intl.formatMessage({ id: 'servicepanel.body.formdata.add', defaultMessage: 'Adicionar campo' })}
                                        nameLabel={intl.formatMessage({ id: 'servicepanel.body.formdata.name', defaultMessage: 'Campo' })}
                                        valueLabel={intl.formatMessage({ id: 'servicepanel.body.formdata.value', defaultMessage: 'Valor' })}
                                        withAssociation
                                        associationOptions={associationList}
                                    />
                                )}

                                {service.requestBodyType === 4 && (
                                    <KeyValueList
                                        rows={service.bodyUrlEncoded}
                                        onAdd={addUrlEncRow}
                                        onRemove={removeUrlEncRow}
                                        onUpdate={updateUrlEncRow as any}
                                        onOpenPicker={openPicker}
                                        addLabel={intl.formatMessage({ id: 'servicepanel.body.urlenc.add', defaultMessage: 'Adicionar campo' })}
                                        nameLabel={intl.formatMessage({ id: 'servicepanel.body.urlenc.name', defaultMessage: 'Chave' })}
                                        valueLabel={intl.formatMessage({ id: 'servicepanel.body.urlenc.value', defaultMessage: 'Valor' })}
                                        withAssociation
                                        associationOptions={associationList}
                                    />
                                )}

                                {service.requestBodyType === 1 && (
                                    <Typography variant="caption" color="text.secondary">
                                        {intl.formatMessage({ id: 'servicepanel.body.none.notice', defaultMessage: 'Nenhum body enviado (adequado pra GET/DELETE).' })}
                                    </Typography>
                                )}
                            </Stack>
                        )}

                        {tab === 5 && (
                            <Stack spacing={2}>
                                {/* Fix F7 sprint 0.5-B rev: aba "Retorno" COMPLETA — todos
                                    os 13 campos do CRUD legado /integration-service-edit.
                                    3 secoes logicas: Objeto/Erro, Lista, Persistencia.
                                    Semantica identica ao CRUD legado — o BE decide o que
                                    fazer com cada campo em runtime; Panel so os edita. */}
                                <Alert severity="info" sx={{ fontSize: 12 }}>
                                    {intl.formatMessage({
                                        id: 'servicepanel.response.notice.full',
                                        defaultMessage: 'Configuração de como o retorno do service é tratado: mapeamento de erro, renderização de lista de opções e persistência de chaves.',
                                    })}
                                </Alert>

                                {/* ─── Secao 1: Objeto de origem e mapeamento de erro ─── */}
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mt: 1 }}>
                                    {intl.formatMessage({ id: 'servicepanel.response.section.error', defaultMessage: 'Objeto de origem e tratamento de erro' })}
                                </Typography>
                                <TextField
                                    size="small"
                                    label={intl.formatMessage({ id: 'servicepanel.response.objectname', defaultMessage: 'Nome do objeto de origem' })}
                                    placeholder="Ex.: data"
                                    helperText={intl.formatMessage({ id: 'servicepanel.response.objectname.help', defaultMessage: 'Chave raiz do JSON de retorno onde estão os dados. Vazio = usa o payload direto.' })}
                                    value={service.objectName ?? ''}
                                    onChange={(e) => patchService({ objectName: e.target.value })}
                                    inputProps={{ maxLength: 255 }}
                                    fullWidth
                                />
                                <TextField
                                    size="small"
                                    label={intl.formatMessage({ id: 'servicepanel.response.nameerrorfield', defaultMessage: 'Nome do campo indicativo de erro' })}
                                    placeholder="Ex.: hasError"
                                    helperText={intl.formatMessage({ id: 'servicepanel.response.nameerrorfield.help', defaultMessage: 'Chave do JSON que sinaliza se houve erro no retorno.' })}
                                    value={service.nameErrorField ?? ''}
                                    onChange={(e) => patchService({ nameErrorField: e.target.value })}
                                    inputProps={{ maxLength: 255 }}
                                    fullWidth
                                />
                                <TextField
                                    select
                                    size="small"
                                    label={intl.formatMessage({ id: 'servicepanel.response.errorfieldreturntype', defaultMessage: 'Tipo de retorno do campo indicativo de erro' })}
                                    value={service.errorFieldReturnType ?? ''}
                                    onChange={(e) => patchService({ errorFieldReturnType: e.target.value === '' ? null : Number(e.target.value) })}
                                    fullWidth
                                >
                                    <MenuItem value="">
                                        <em>{intl.formatMessage({ id: 'servicepanel.response.errorfieldreturntype.none', defaultMessage: '— Nenhum —' })}</em>
                                    </MenuItem>
                                    {ERROR_FIELD_RETURN_TYPES.map(t => (
                                        <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    size="small"
                                    label={intl.formatMessage({ id: 'servicepanel.response.valueerrorfield', defaultMessage: 'Valor que indica erro ocorrido' })}
                                    placeholder="Ex.: true"
                                    helperText={intl.formatMessage({ id: 'servicepanel.response.valueerrorfield.help', defaultMessage: 'Valor do campo acima que sinaliza erro (comparação exata).' })}
                                    value={service.valueErrorField ?? ''}
                                    onChange={(e) => patchService({ valueErrorField: e.target.value })}
                                    inputProps={{ maxLength: 255 }}
                                    fullWidth
                                />
                                <TextField
                                    size="small"
                                    label={intl.formatMessage({ id: 'servicepanel.response.errordatafieldname', defaultMessage: 'Nome do campo com dados de erro' })}
                                    placeholder="Ex.: errorDetails"
                                    helperText={intl.formatMessage({ id: 'servicepanel.response.errordatafieldname.help', defaultMessage: 'Chave do JSON que contém a descrição/detalhe do erro.' })}
                                    value={service.errorDataFieldName ?? ''}
                                    onChange={(e) => patchService({ errorDataFieldName: e.target.value })}
                                    inputProps={{ maxLength: 255 }}
                                    fullWidth
                                />
                                <TextField
                                    size="small"
                                    multiline
                                    rows={3}
                                    label={intl.formatMessage({ id: 'servicepanel.response.messageerror', defaultMessage: 'Mensagem enviada em caso de erro' })}
                                    helperText={intl.formatMessage({ id: 'servicepanel.response.messageerror.help', defaultMessage: 'Texto exibido pro cliente quando o retorno indicar erro. Aceita placeholders.' })}
                                    value={service.messageError ?? ''}
                                    onChange={(e) => patchService({ messageError: e.target.value })}
                                    fullWidth
                                />
                                <TextField
                                    size="small"
                                    multiline
                                    rows={3}
                                    label={intl.formatMessage({ id: 'servicepanel.response.notfounderrormessage', defaultMessage: 'Mensagem em caso de objeto de retorno não encontrado' })}
                                    helperText={intl.formatMessage({ id: 'servicepanel.response.notfounderrormessage.help', defaultMessage: 'Texto exibido pro cliente quando o objeto de origem não estiver no retorno.' })}
                                    value={service.notFoundErrorMessage ?? ''}
                                    onChange={(e) => patchService({ notFoundErrorMessage: e.target.value })}
                                    fullWidth
                                />

                                <Divider sx={{ my: 1 }} />

                                {/* ─── Secao 2: Mapeamento de campos de valor e exibicao ───
                                    Fix F7 sprint 0.5-B rev: retirado o `disabled={!isArrayObjectType}`
                                    dos campos nameFieldValue/nameFieldDisplay. Semantica canonica
                                    (identica ao CRUD legado /integration-service-edit): esses 4
                                    campos sao do RETORNO em geral — o JSON de resposta pode ser um
                                    objeto simples com campos a extrair OU um array a renderizar
                                    como lista. isArrayObjectType decide o COMPORTAMENTO de render
                                    (lista vs objeto unico), mas nao invalida os campos. */}
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {intl.formatMessage({ id: 'servicepanel.response.section.mapping', defaultMessage: 'Mapeamento de campos de valor e exibição' })}
                                </Typography>
                                <Alert severity="info" sx={{ fontSize: 12 }}>
                                    {intl.formatMessage({
                                        id: 'servicepanel.response.notice',
                                        defaultMessage: 'Extração dos campos do retorno. Funciona pra objeto único (extrai um campo) e pra lista (renderiza opções pro cliente escolher, quando "Retorno é uma lista" estiver ligado abaixo).',
                                    })}
                                </Alert>
                                <TextField
                                    size="small"
                                    label={intl.formatMessage({ id: 'servicepanel.response.namefieldvalue', defaultMessage: 'Nome do campo de valor' })}
                                    placeholder="Ex.: id"
                                    helperText={intl.formatMessage({ id: 'servicepanel.response.namefieldvalue.help', defaultMessage: 'Chave do JSON de retorno que vira o valor selecionado (guardado na variável de associação).' })}
                                    value={service.nameFieldValue ?? ''}
                                    onChange={(e) => patchService({ nameFieldValue: e.target.value })}
                                    inputProps={{ maxLength: 255 }}
                                    fullWidth
                                />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        {intl.formatMessage({ id: 'servicepanel.response.variableidfieldvalue', defaultMessage: 'Associação do campo de valor (variável)' })}
                                    </Typography>
                                    <AssociationSelector
                                        value={
                                            service.variableIdFieldValue != null
                                                ? { type: 'variable', relationId: service.variableIdFieldValue }
                                                : null
                                        }
                                        options={associationList.filter(o => (o.type ?? '').toLowerCase().startsWith('var'))}
                                        onChange={(rel) => patchService({ variableIdFieldValue: rel?.relationId ?? null })}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {intl.formatMessage({ id: 'servicepanel.response.variableidfieldvalue.help', defaultMessage: 'Variável que recebe o valor extraído/selecionado — disponível pras caixas seguintes do fluxo.' })}
                                    </Typography>
                                </Box>
                                <TextField
                                    size="small"
                                    label={intl.formatMessage({ id: 'servicepanel.response.namefielddisplay', defaultMessage: 'Nome do campo de exibição' })}
                                    placeholder="Ex.: titulo"
                                    helperText={intl.formatMessage({ id: 'servicepanel.response.namefielddisplay.help', defaultMessage: 'Chave do JSON de retorno usada como rótulo (quando o retorno é lista, cada item é apresentado com esse texto).' })}
                                    value={service.nameFieldDisplay ?? ''}
                                    onChange={(e) => patchService({ nameFieldDisplay: e.target.value })}
                                    inputProps={{ maxLength: 255 }}
                                    fullWidth
                                />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        {intl.formatMessage({ id: 'servicepanel.response.variableidfielddisplay', defaultMessage: 'Associação do campo de exibição (variável)' })}
                                    </Typography>
                                    <AssociationSelector
                                        value={
                                            service.variableIdFieldDisplay != null
                                                ? { type: 'variable', relationId: service.variableIdFieldDisplay }
                                                : null
                                        }
                                        options={associationList.filter(o => (o.type ?? '').toLowerCase().startsWith('var'))}
                                        onChange={(rel) => patchService({ variableIdFieldDisplay: rel?.relationId ?? null })}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {intl.formatMessage({ id: 'servicepanel.response.variableidfielddisplay.help', defaultMessage: 'Opcional: variável que recebe o texto de exibição do item selecionado.' })}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <Switch
                                        checked={!!service.isArrayObjectType}
                                        onChange={(e) => patchService({ isArrayObjectType: e.target.checked })}
                                    />
                                    <Box>
                                        <Typography variant="body2">
                                            {intl.formatMessage({ id: 'servicepanel.response.isarray', defaultMessage: 'Retorno é uma lista?' })}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {intl.formatMessage({ id: 'servicepanel.response.isarray.help', defaultMessage: 'Ativa a renderização como lista de opções clicáveis (WhatsApp/Webchat). Desligado = extrai um único objeto do retorno.' })}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 1 }} />

                                {/* ─── Secao 3: Persistencia ─── */}
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {intl.formatMessage({ id: 'servicepanel.response.section.persistence', defaultMessage: 'Persistência' })}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Switch
                                        checked={!!service.saveKeyApplication}
                                        onChange={(e) => patchService({ saveKeyApplication: e.target.checked })}
                                    />
                                    <Typography variant="body2">
                                        {intl.formatMessage({ id: 'servicepanel.response.savekey', defaultMessage: 'Este serviço salva alguma informação?' })}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({ id: 'servicepanel.response.savekey.help', defaultMessage: 'Marque quando o retorno contém dados que precisam ser persistidos (chaves de aplicação, tokens externos, etc).' })}
                                </Typography>
                            </Stack>
                        )}

                        {tab === 4 && (
                            <Stack spacing={2}>
                                {/* Fix F7 sprint 0.5-Auth rev: paridade 100% com CRUD legado
                                    (/integration-service-edit linhas 982-1002):
                                     - Dropdown "Método de autenticação" (methodAuthList vindo
                                       do mesmo endpoint canonico GET /integrationservice, campo
                                       `authenticationmethodintegration`).
                                     - Dropdown "Autenticação cadastrada" (integrationAuthList
                                       do mesmo payload, campo `integrationauth`).
                                     - Ambos sempre editaveis. Sem disabled/auto-clear (CRUD
                                       legado nao faz). Required visual quando aplicavel — mesma
                                       regra do CRUD: `methodAuth?.id !== 3` (id 3 = None). */}
                                <TextField
                                    select
                                    size="small"
                                    label={intl.formatMessage({ id: 'servicepanel.auth.method', defaultMessage: 'Método de autenticação' })}
                                    value={service.methodAuth ?? ''}
                                    onChange={(e) => {
                                        const v = e.target.value === '' ? null : Number(e.target.value);
                                        patchService({ methodAuth: v });
                                    }}
                                    fullWidth
                                >
                                    <MenuItem value="">
                                        <em>{intl.formatMessage({ id: 'servicepanel.auth.method.none', defaultMessage: '— Selecione —' })}</em>
                                    </MenuItem>
                                    {methodAuthList.map(m => (
                                        <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    select
                                    size="small"
                                    label={intl.formatMessage({ id: 'servicepanel.auth.integrationauth', defaultMessage: 'Autenticação' })}
                                    value={service.integrationAuthId ?? ''}
                                    onChange={(e) => {
                                        const v = e.target.value === '' ? null : Number(e.target.value);
                                        patchService({ integrationAuthId: v });
                                    }}
                                    helperText={
                                        service.methodAuth != null && service.methodAuth !== 3
                                            ? intl.formatMessage({ id: 'servicepanel.auth.integrationauth.required', defaultMessage: 'Obrigatório quando o método de autenticação está preenchido.' })
                                            : undefined
                                    }
                                    fullWidth
                                >
                                    <MenuItem value="">
                                        <em>{intl.formatMessage({ id: 'servicepanel.auth.integrationauth.none', defaultMessage: '— Nenhuma —' })}</em>
                                    </MenuItem>
                                    {integrationAuthList.map(a => (
                                        <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                        )}
                    </>
                )}
            </Box>

            {/* Footer */}
            {(service.id || isCreating) && (
                <Box
                    sx={{
                        px: 2, py: 1.5,
                        borderTop: '1px solid rgba(0,0,0,0.08)',
                        display: 'flex', gap: 1, alignItems: 'center',
                        background: '#fafafa',
                    }}
                >
                    {saveError && (
                        <Typography variant="caption" color="error" sx={{ flex: 1 }}>{saveError}</Typography>
                    )}
                    {!saveError && <Box sx={{ flex: 1 }} />}
                    {isCreating && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                // Cancela criacao — se havia service selecionado antes, recarrega.
                                // Se nao havia, limpa selecao completa (volta pro empty state).
                                setIsCreating(false);
                                setSaveError(null);
                                setDirty(false);
                                if (currentServiceId) {
                                    loadServiceDetail(currentServiceId);
                                } else {
                                    setService(EMPTY_SERVICE);
                                }
                            }}
                        >
                            {intl.formatMessage({ id: 'servicepanel.cancel', defaultMessage: 'Cancelar' })}
                        </Button>
                    )}
                    {service.id && !isCreating && (
                        <Button size="small" startIcon={<ScienceIcon />} onClick={() => setTestOpen(true)}>
                            {intl.formatMessage({ id: 'servicepanel.test', defaultMessage: 'Testar' })}
                        </Button>
                    )}
                    <Button
                        size="small" variant="contained"
                        disabled={!dirty || saving}
                        onClick={handleSave}
                    >
                        {saving
                            ? intl.formatMessage({ id: 'servicepanel.saving', defaultMessage: 'Salvando...' })
                            : intl.formatMessage({ id: 'servicepanel.save', defaultMessage: 'Salvar' })}
                    </Button>
                </Box>
            )}

            {/* Dialog testar — regra CLAUDE.md: DialogContent NAO respeita overflowY/maxHeight
                via sx (flex do MUI ignora). Solucao: envolver conteudo com <div style INLINE>
                — barra de rolagem aparece naturalmente e nada corta ao expandir resultado. */}
            <Dialog
                open={testOpen}
                maxWidth="lg"
                fullWidth
                onClose={() => setTestOpen(false)}
                PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {intl.formatMessage({ id: 'servicepanel.test.title', defaultMessage: 'Testar serviço' })}
                    <IconButton
                        size="small"
                        onClick={() => setTestOpen(false)}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '16px 20px' }}>
                        {service.id && (
                            <RequestIntegrationService integrationServiceId={service.id} isOpenFromJoint />
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* F7 Fase 1: PlaceholderPicker unificado (substitui ChatVariablesPicker
                + LegacyVariablesPicker). 6 categorias em abas, fonte /get-placeholders. */}
            <PlaceholderPicker
                open={!!pickerAnchor}
                anchorEl={pickerAnchor}
                onClose={closePicker}
                onInsert={insertVariable}
            />

            {/* Toast (F6 rodada 6) — Snackbar local pra feedback pos-save.
                Bottom-right, autoclose 4s, mesmo padrao Postman/Zendesk. */}
            <Snackbar
                open={!!toast}
                autoHideDuration={4000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                sx={{ zIndex: 2000 }}
            >
                <Alert
                    severity={toast?.severity ?? 'success'}
                    onClose={() => setToast(null)}
                    variant="filled"
                    sx={{ boxShadow: 3 }}
                >
                    {toast?.message}
                </Alert>
            </Snackbar>
        </>
    );

    if (variant === 'floating') {
        return (
            <>
                <Paper elevation={8} sx={{ ...containerSx, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 2 }}>
                    {content}
                    {/* Handle de resize no canto inferior direito — 14x14 clicavel.
                        Duas linhas diagonais dao o visual clasico de "grip". */}
                    <Box
                        onMouseDown={onResizeMouseDown}
                        sx={{
                            position: 'absolute',
                            right: 2, bottom: 2,
                            width: 14, height: 14,
                            cursor: 'nwse-resize',
                            zIndex: 10,
                            '&::before': {
                                content: '""',
                                position: 'absolute', right: 3, bottom: 3,
                                width: 7, height: 1, background: 'rgba(0,0,0,0.4)',
                                transform: 'rotate(-45deg)', transformOrigin: 'right bottom',
                            },
                            '&::after': {
                                content: '""',
                                position: 'absolute', right: 3, bottom: 6,
                                width: 4, height: 1, background: 'rgba(0,0,0,0.4)',
                                transform: 'rotate(-45deg)', transformOrigin: 'right bottom',
                            },
                        }}
                    />
                </Paper>
                {/* Drop-zone highlight — overlay verde translucido sobre a area do
                    Inspector docked enquanto o usuario arrasta o painel por cima.
                    Ao soltar, `onDragEnd` chama `onToggleFloat` = ACOPLAR (F6.G). */}
                {dropTargetActive && (() => {
                    const el = document.querySelector('.chatbot-inspector') as HTMLElement | null;
                    if (!el) return null;
                    const rect = el.getBoundingClientRect();
                    return (
                        <Box
                            sx={{
                                position: 'fixed',
                                left: rect.left, top: rect.top,
                                width: rect.width, height: rect.height,
                                zIndex: 499, // 1 abaixo do painel (500) — nao intercepta drag
                                pointerEvents: 'none',
                                background: 'rgba(76, 175, 80, 0.18)',
                                border: '3px dashed #4caf50',
                                display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, color: '#2e7d32',
                                fontSize: 16,
                            }}
                        >
                            {intl.formatMessage({
                                id: 'servicepanel.dockhere',
                                defaultMessage: 'Soltar aqui pra acoplar',
                            })}
                        </Box>
                    );
                })()}
            </>
        );
    }

    return (
        <Box sx={{ ...containerSx, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {content}
        </Box>
    );
};

// ─── Subcomponente: TextField com botao {} ──────────────────────────────
type VariableTextFieldProps = {
    label: string;
    value: string;
    onChange: (v: string) => void;
    onOpenPicker: (anchor: HTMLElement, input: HTMLInputElement | HTMLTextAreaElement | null) => void;
    fullWidth?: boolean;
    multiline?: boolean;
    minRows?: number;
    monospace?: boolean;
};

const VariableTextField: React.FC<VariableTextFieldProps> = ({ label, value, onChange, onOpenPicker, fullWidth, multiline, minRows, monospace }) => {
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    return (
        <TextField
            label={label}
            size="small"
            fullWidth={fullWidth}
            multiline={multiline}
            minRows={minRows}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            inputRef={inputRef}
            InputProps={{
                endAdornment: (
                    <Tooltip title="Inserir variável do chat">
                        <IconButton
                            size="small"
                            onClick={(e) => onOpenPicker(e.currentTarget, inputRef.current)}
                            sx={{ mr: -0.5 }}
                        >
                            <CodeIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ),
            }}
            sx={monospace ? {
                '& .MuiInputBase-input, & .MuiInputBase-inputMultiline': {
                    fontFamily: "'Fira Code', 'Consolas', monospace",
                    fontSize: 12,
                    lineHeight: 1.5,
                },
            } : undefined}
        />
    );
};

// ─── Subcomponente: AssociationSelector ─────────────────────────────────
// Autocomplete inline com params/vars/customFields do fluxo. Se o usuario
// selecionar uma associacao, o campo Value da linha vira read-only (o valor
// vem da associacao no runtime). Null = valor literal.
type AssociationSelectorProps = {
    value: AssociationRelation;
    options: AssociationOption[];
    onChange: (rel: AssociationRelation) => void;
};

const AssociationSelector: React.FC<AssociationSelectorProps> = ({ value, options, onChange }) => {
    // Encontra opcao atual pra pre-selecionar no Autocomplete.
    const currentOption = value
        ? options.find(o => {
            const typeMatch = value.type === 'param' ? o.type?.toLowerCase().startsWith('param')
                : value.type === 'variable' ? o.type?.toLowerCase().startsWith('var')
                : o.type?.toLowerCase().includes('custom');
            return typeMatch && o.relationid === value.relationId;
        }) ?? null
        : null;

    return (
        <Autocomplete
            size="small"
            options={options}
            value={currentOption}
            getOptionLabel={(o) => {
                const t = o.type?.toLowerCase() ?? '';
                const prefix = t.startsWith('param') ? '%' : t.startsWith('var') ? '@' : '$';
                const suffix = t.startsWith('param') ? '%' : t.startsWith('var') ? '@' : '$';
                return `${prefix}${o.name}${suffix}`;
            }}
            isOptionEqualToValue={(a, b) => a?.relationid === b?.relationid && a?.type === b?.type}
            groupBy={(o) => {
                const t = o.type?.toLowerCase() ?? '';
                return t.startsWith('param') ? 'Parâmetros'
                    : t.startsWith('var') ? 'Variáveis'
                    : 'Campos personalizados';
            }}
            onChange={(_, val) => {
                if (!val) { onChange(null); return; }
                const t = val.type?.toLowerCase() ?? '';
                const type: AssociationRelation['type'] =
                    t.startsWith('param') ? 'param'
                    : t.startsWith('var') ? 'variable'
                    : 'customField';
                onChange({ type, relationId: val.relationid, name: val.name });
            }}
            renderInput={(params) => (
                <TextField {...params} placeholder="Associação" />
            )}
            sx={{ minWidth: 180 }}
            slotProps={{
                popper: {
                    modifiers: [
                        { name: 'flip', enabled: true, options: { fallbackPlacements: ['top', 'bottom'] } },
                        { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport' } },
                    ],
                },
            }}
        />
    );
};

// ─── Subcomponente: lista name/value editavel + associacao (F6.E) ─────
type KeyValueListProps = {
    rows: Array<{ id?: number; name: string; value: string; relation?: AssociationRelation }>;
    onAdd: () => void;
    onRemove: (idx: number) => void;
    onUpdate: (idx: number, field: 'name' | 'value' | 'relation', value: any) => void;
    onOpenPicker: (anchor: HTMLElement, input: HTMLInputElement | HTMLTextAreaElement | null) => void;
    addLabel: string;
    nameLabel: string;
    valueLabel: string;
    /** Se true, mostra coluna Associação (Params, form-data, url-encoded).
     * Se false, só name/value (Headers). */
    withAssociation?: boolean;
    associationOptions?: AssociationOption[];
};

const KeyValueList: React.FC<KeyValueListProps> = ({ rows, onAdd, onRemove, onUpdate, onOpenPicker, addLabel, nameLabel, valueLabel, withAssociation, associationOptions }) => {
    // Layout empilhado 2 linhas (padrao Postman/Insomnia em painel estreito):
    //   Linha 1: [ Nome .................... ] [X]
    //   Linha 2: [ Valor ............ ] [ Associação ]   (se withAssociation)
    //            [ Valor .................. ]            (senão)
    // Evita quebra caotica em <500px e mantem tudo alinhado.
    return (
        <Stack spacing={1.25}>
            {rows.map((r, idx) => {
                const hasAssoc = withAssociation && !!r.relation;
                return (
                    <Box
                        key={idx}
                        sx={{
                            border: '1px solid rgba(0,0,0,0.1)',
                            borderRadius: 1,
                            p: 1,
                            background: '#fbfbfb',
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                label={nameLabel} size="small" value={r.name}
                                onChange={(e) => onUpdate(idx, 'name', e.target.value)}
                                sx={{ flex: 1 }}
                            />
                            <IconButton size="small" onClick={() => onRemove(idx)}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                            {hasAssoc ? (
                                <Chip
                                    label={r.relation?.name ? `${r.relation.type}: ${r.relation.name}` : `Associação #${r.relation?.relationId}`}
                                    size="small"
                                    onDelete={() => onUpdate(idx, 'relation', null)}
                                    sx={{ flex: 1, justifyContent: 'flex-start' }}
                                />
                            ) : (
                                <Box sx={{ flex: 1 }}>
                                    <VariableTextField
                                        label={valueLabel}
                                        value={r.value}
                                        onChange={(v) => onUpdate(idx, 'value', v)}
                                        onOpenPicker={onOpenPicker}
                                        fullWidth
                                    />
                                </Box>
                            )}
                            {withAssociation && (
                                <Box sx={{ width: 180 }}>
                                    <AssociationSelector
                                        value={r.relation ?? null}
                                        options={associationOptions ?? []}
                                        onChange={(rel) => onUpdate(idx, 'relation', rel)}
                                    />
                                </Box>
                            )}
                        </Stack>
                    </Box>
                );
            })}
            <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>{addLabel}</Button>
        </Stack>
    );
};

export default ServicePropertiesPanel;
